import { QueryParamConfig, decodeSingleQueryParam, useQueryParams, useRemoteData } from '@binhatch/hooks';
import { translations } from '@binhatch/locale';
import {
  AsyncButton,
  BonusValue,
  Card,
  DynamicBackButton,
  EmptyState,
  LoadingState,
  PageLoaderHeading,
  Pagination,
  PromotionArticles,
  PromotionClaimed,
  PromotionImage,
  PromotionPeriod
} from '@binhatch/ui';
import { fromPromotion, getAllFromApi, getCurrentTargetLevelIndex, getProgressForPeriod, getTotalValueFromProgress } from '@binhatch/utility';
import { Period, Progress, PromotionSortByField, PromotionType, SortDirection, TargetLevel } from '@flexinet/flexinet-api';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { productApi, progressApi, promotionApi } from '@/integrations/api';
import { fireConfetti } from '@/utils/confetti';

import { Auth } from '@/containers/useAuth';

import { useBalance } from '@/hooks/useBalance';

import { PeriodExpiration } from './components/PeriodExpiration';

const page: QueryParamConfig<string | undefined> = {
  decode: (value) => decodeSingleQueryParam(value, undefined),
  encode: (value) => value
};

const config = { page };

export const PromotionClaimListPage: React.FC = () => {
  const { reload } = useBalance();
  const { context } = Auth.useContainer();

  const [query, updateQuery] = useQueryParams({ config });
  const [claimed, setClaimed] = React.useState<string[]>([]);

  const promotions = useRemoteData({ key: `useClaimablePromotions`, page: query.page }, ({ page }) =>
    promotionApi
      .listUserPromotions(
        page,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        PromotionSortByField.EndsAt,
        SortDirection.Asc,
        true,
        PromotionType.Regular
      )
      .then((r) => r.data)
      .then((r) => ({ ...r, promotions: r.promotions.map(fromPromotion) }))
      .then(async ({ promotions, ...r }) => {
        const promotionIds = promotions.map((p) => p.id);

        const progress: Progress[] = promotionIds.length
          ? await getAllFromApi(
              () => progressApi.listUserProgress(promotionIds).then((r) => r.data),
              (r) => r.data
            )
          : [];

        return {
          ...r,
          promotions: promotions.map((promotion) => {
            const rewards =
              promotion.periods?.reduce<{ period: Period; level: TargetLevel }[]>((rewards, period) => {
                const value = getTotalValueFromProgress(getProgressForPeriod(progress, promotion, period));

                const index = getCurrentTargetLevelIndex(promotion.target.levels, value);

                const level = promotion.target.levels[index];

                rewards.push({ period, level });

                return rewards;
              }, []) ?? [];

            return { promotion, rewards };
          })
        };
      })
  );

  return (
    <main className="space-y-2 md:space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div>
            <DynamicBackButton />

            <PageLoaderHeading loading={promotions.isLoading || promotions.isValidating}>
              <FormattedMessage id={translations.pages.promotionClaimList.title} />
            </PageLoaderHeading>
          </div>
        </div>
      </div>

      <LoadingState loading={promotions.isLoading || promotions.isValidating}>
        {!promotions.isLoading && !promotions.data?.promotions.length && (
          <EmptyState>
            <FormattedMessage id={translations.pages.promotionList.empty} />
          </EmptyState>
        )}

        <ul className="grid gap-4">
          {promotions.data?.promotions.map(({ promotion, rewards }) => (
            <Card className="flex flex-col gap-4 shadow" key={promotion.id}>
              <div className="flex flex-wrap gap-4">
                <div className="mr-4 w-20 flex-shrink-0">
                  <PromotionImage image={promotion.imageURL} logo={promotion.brandLogoURL} />
                </div>

                <div className="flex flex-1 flex-col gap-2">
                  <div className="font-semibold">{promotion.name}</div>

                  <PromotionArticles tags={promotion.tags} />

                  <div className="line-clamp-2 max-w-3xl">{promotion.description}</div>
                </div>

                <div className="w-full lg:w-auto">
                  {claimed.includes(promotion.id) ? (
                    <PromotionClaimed />
                  ) : (
                    <AsyncButton
                      appearance="primary"
                      className="h-10 w-full px-4"
                      disabled={context?.client?.isSuspended}
                      onClick={async (event: MouseEvent) => {
                        await promotionApi.claimPromotionUserAll(promotion.id);
                        setClaimed((claimed) => [...claimed, promotion.id]);
                        fireConfetti(event.clientX, event.clientY);
                        await reload();
                      }}
                    >
                      <FormattedMessage id={translations.pages.promotionClaimList.claim} />
                    </AsyncButton>
                  )}
                </div>
              </div>

              <ul className="flex flex-wrap gap-2">
                {rewards?.map(({ period, level }, index) => (
                  <li className="border-shade flex gap-4 rounded border p-2" key={index}>
                    <div>
                      <PromotionPeriod endAt={new Date(period.endAt)} startAt={new Date(period.startAt)} />
                    </div>

                    <BonusValue
                      getProduct={(id) => productApi.getUserProductById(id).then((r) => r.data)}
                      reference={level.bonus.ref}
                      unit={level.bonus.mu}
                      value={level.bonus.value}
                    />

                    <PeriodExpiration {...{ period }} />
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </ul>
      </LoadingState>

      <Pagination
        hasNext={!!promotions.data?.nextToken}
        hasPrevious={!!promotions.data?.prevToken}
        onNext={() => updateQuery({ page: promotions.data?.nextToken })}
        onPrevious={() => updateQuery({ page: promotions.data?.prevToken })}
      />
    </main>
  );
};
